/**
 * The one way this app says "there is nothing here".
 *
 * ─── Why one component and not fifteen ───────────────────────────────────────
 *
 * Every list, table, panel and picker in the product eventually has nothing to
 * show, and before this each of them answered that in its own hand. Some drew a
 * bordered card around the void and some floated the same block on the bare
 * page. The icon was passed in by the caller, so a fixed 48px box received
 * icons at 24, 28, 32, 40 and 48px, and the dashboard managed to show three of
 * those sizes at once. Titles were sometimes a label and sometimes a sentence
 * with a full stop. That is what this file exists to stop.
 *
 * The caller now chooses WHAT to say. This component owns HOW it looks: the
 * icon size, the spacing, the alignment, the button emphasis and the
 * announcement. There is deliberately no `card`/`inline` switch and no padding
 * prop, because those were the two knobs the drift came out of.
 *
 * ─── "Empty" is three different states, not one ──────────────────────────────
 *
 * Unifying the styling must not flatten the meaning. There are three:
 *
 *   first-run    The collection has never had anything in it. The user's next
 *                move is to create the first item, so the action is the filled
 *                primary button: it is the most useful thing on the screen.
 *
 *   no-matches   Rows exist; a search or filter is excluding all of them.
 *                Offering "create your first contact" to somebody who just
 *                mistyped a name is wrong and reads as broken. The action here
 *                clears the filter, and it is an outline button because
 *                correcting a filter is a step back to where they were, not the
 *                goal. This state appears in response to typing, so it is a
 *                live region: a screen reader user gets told the list emptied.
 *
 *   unavailable  We cannot show it: the request failed, the record is gone, or
 *                the viewer does not hold the permission. A create button here
 *                is worse than useless because the action would fail. The
 *                action is a retry or a way back, and it is never a create.
 *
 * `no-matches` and `unavailable` supply their own icon, so those two states
 * look identical everywhere in the app and become recognisable on sight.
 * `first-run` takes the collection's own icon, because "no locations yet"
 * wants a pin.
 *
 * ─── Two sizes, on purpose ───────────────────────────────────────────────────
 *
 * One styling does not mean one scale. `page` owns the content area of a route
 * or a full settings tab. `panel` is for a side sheet, a popover list or a
 * small card section, where a 40px icon and a 360px tall band would be a
 * regression. Pick by the width of the container, not by taste, and never
 * reach for `className` to split the difference: per-call-site padding is
 * exactly how the field heights drifted earlier on this branch.
 *
 * Not covered here, deliberately: a single greyed line inside a dropdown or a
 * command list ("No labels found."). That is a menu affordance in a 208px
 * popover, and dropping an icon-and-heading block into it would be the
 * regression this component is meant to prevent. Those stay as they are.
 */
import { AlertCircle, SearchX, type LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/** Which of the three "empty" situations this is. See the docblock. */
export type EmptyStateKind = 'first-run' | 'no-matches' | 'unavailable';

/**
 * How much room the state has. `page` = a route's content area or a full
 * settings tab. `panel` = a sheet, a popover list, a small card section.
 */
export type EmptyStateSize = 'page' | 'panel';

export interface EmptyStateAction {
  label: string;
  /** In-page action. Mutually exclusive with `href`. */
  onClick?: () => void;
  /** Navigation. An external one opens in a new tab with the usual noopener. */
  href?: string;
  external?: boolean;
  /** Shows a spinner and blocks re-entry while the action is in flight. */
  loading?: boolean;
}

export interface EmptyStateProps {
  /** Defaults to `first-run`, which is the common case. */
  kind?: EmptyStateKind;
  /** Defaults to `page`. */
  size?: EmptyStateSize;
  /**
   * The collection's own icon, for `first-run`. Pass the component itself
   * (`Users`), not an element: the size is this component's to decide, and
   * letting the caller pass `<Users className="h-6 w-6" />` is how five icon
   * sizes got into the app. Ignored for the other two kinds, which have one
   * icon each so they read the same everywhere.
   */
  icon?: LucideIcon;
  /** A short statement of fact, sentence case, no full stop. */
  title: string;
  /** One sentence, ending in a full stop, saying what fills this space. */
  description?: string;
  /** The thing that resolves this state. Offer it whenever one exists. */
  action?: EmptyStateAction;
  /** A lower-emphasis alternative, sitting to the right of `action`. */
  secondaryAction?: EmptyStateAction;
  /**
   * Positioning only (margins, grid placement). Do not use it to change the
   * padding, the alignment or the type scale. That is what `size` is for.
   */
  className?: string;
}

const SIZES = {
  page: {
    // The min-height replaces the `flex min-h-[360px] items-center` wrapper that
    // four pages had each grown their own copy of.
    root: 'min-h-[360px] px-6 py-12',
    icon: 'h-10 w-10',
    title: 'text-lg',
    body: 'max-w-md',
    gap: 'mt-5',
  },
  panel: {
    root: 'px-4 py-8',
    icon: 'h-7 w-7',
    title: 'text-base',
    body: 'max-w-sm',
    gap: 'mt-4',
  },
} as const satisfies Record<EmptyStateSize, Record<string, string>>;

/** The two states that always look the same, whatever list they appear in. */
const FIXED_ICONS: Partial<Record<EmptyStateKind, LucideIcon>> = {
  'no-matches': SearchX,
  unavailable: AlertCircle,
};

function ActionButton({
  action,
  variant,
}: {
  action: EmptyStateAction;
  variant: 'default' | 'outline' | 'ghost';
}) {
  if (action.href) {
    return (
      <Button variant={variant} asChild>
        <a
          href={action.href}
          target={action.external ? '_blank' : undefined}
          rel={action.external ? 'noreferrer noopener' : undefined}
        >
          {action.label}
        </a>
      </Button>
    );
  }
  return (
    <Button variant={variant} onClick={action.onClick} loading={action.loading}>
      {action.label}
    </Button>
  );
}

export function EmptyState({
  kind = 'first-run',
  size = 'page',
  icon,
  title,
  description,
  action,
  secondaryAction,
  className,
}: EmptyStateProps) {
  const s = SIZES[size];
  const Icon = FIXED_ICONS[kind] ?? icon;

  // `first-run` and `unavailable` are already on screen when the page settles,
  // so they are read as ordinary content. `no-matches` appears mid-keystroke in
  // place of rows that were there a moment ago; without a live region a screen
  // reader user gets silence and assumes the search is still running.
  const live = kind === 'no-matches';

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center',
        s.root,
        className,
      )}
      {...(live ? { role: 'status' } : {})}
    >
      {Icon && (
        <Icon
          aria-hidden="true"
          strokeWidth={1.5}
          className={cn('mb-4 text-muted-foreground/60', s.icon)}
        />
      )}
      <h3 className={cn('font-medium text-foreground', s.title)}>{title}</h3>
      {description && (
        <p className={cn('mt-1.5 text-sm text-muted-foreground', s.body)}>{description}</p>
      )}
      {(action || secondaryAction) && (
        <div className={cn('flex flex-wrap items-center justify-center gap-2', s.gap)}>
          {action && (
            <ActionButton
              action={action}
              // The one thing worth doing on a first-run screen earns a filled
              // button. Clearing a filter or retrying is a correction, so it
              // stays quiet.
              variant={kind === 'first-run' ? 'default' : 'outline'}
            />
          )}
          {secondaryAction && <ActionButton action={secondaryAction} variant="ghost" />}
        </div>
      )}
    </div>
  );
}
